import _ from 'lodash'
import { gql, type ResultOf } from '~/tada/gql'
import { AWARD_RANKS } from '~/stores/archive'

export const AWARD_WINNERS_PATH = '/admin/award-winners'
export const awardWinnersPath = (year: number) => `${AWARD_WINNERS_PATH}/${year}`

/**
 * Every award has a Top Ten split into tiers. A tier's rank is the place its
 * first film finished (Nominees are 3rd–5th, Top Ten 6th–10th), and films in
 * the same tier are unordered.
 */
export const TIERS = [
  { rank: 1, slots: 1 },
  { rank: 2, slots: 1 },
  { rank: 3, slots: 3 },
  { rank: 6, slots: 5 },
].map(t => ({ ...t, ...AWARD_RANKS[t.rank] }))

export type Tier = (typeof TIERS)[number]

export const TOP_TEN = _.sumBy(TIERS, 'slots')

export const tierFor = (rank: number): Tier =>
  _.find(TIERS, { rank }) ?? { rank, slots: 0, label: `Rank ${rank}`, color: 'grey' }

/**
 * Some years ended up with more than one Top Ten per award (2026 has two), so
 * an award holds as many lists as its films need, each with every tier's slots.
 */
export const listCount = (rows: unknown[]) => Math.max(1, Math.ceil(rows.length / TOP_TEN))

/** Every list is full, with each tier holding exactly its share. */
export const isComplete = (rows: { rank: number }[]) => {
  const lists = listCount(rows)
  return (
    rows.length === lists * TOP_TEN &&
    TIERS.every(t => _.filter(rows, { rank: t.rank }).length === t.slots * lists)
  )
}

/** The tier a film finishing in this place (1–10) belongs to. */
export const placeToRank = (place: number) =>
  place >= 1 && place <= TOP_TEN ? _.findLast(TIERS, t => t.rank <= place)?.rank : undefined

/** Best Film first, then alphabetical, matching the Archive. */
export const sortAwards = <T extends { id: number; name?: string | null }>(awards: T[]) =>
  _.orderBy(awards, [a => a.id !== 1, 'name'])

const AwardTeam = gql(`fragment AwardTeam on teams @_unmask {
  id
  team_number
  name
  film_name
  submitted_at
  poster: assets(where: {asset_type_id: {_eq: 21}}, order_by: {created: desc}, limit: 1) {
    id
    file_name
  }
}`)

export const AwardWinnersFestivalsQuery = gql(`query AwardWinnersFestivals {
  film_festivals(order_by: {id: desc}) {
    id
    name
    is_public
    teams_aggregate(where: {submitted_at: {_is_null: false}}) {
      aggregate {
        count
      }
    }
  }
  award_winners(distinct_on: film_festival_id) {
    id
    film_festival_id
  }
}`)

export const AwardWinnersYearQuery = gql(
  `query AwardWinnersYear($festivalId: numeric!, $festivalIdInt: Int!) {
  awards(order_by: {id: asc}) {
    id
    name
    description
  }
  teams(where: {film_festival_id: {_eq: $festivalId}, submitted_at: {_is_null: false}}, order_by: {team_number: asc}) {
    ...AwardTeam
  }
  award_winners(where: {film_festival_id: {_eq: $festivalIdInt}}, order_by: {id: asc}) {
    id
    award_id
    team_id
    rank
    team {
      ...AwardTeam
    }
  }
}`,
  [AwardTeam],
)

export const SaveAwardWinners = gql(`mutation SaveAwardWinners(
  $deleteIds: [Int!]!
  $inserts: [award_winners_insert_input!]!
  $updates: [award_winners_updates!]!
) {
  delete_award_winners(where: {id: {_in: $deleteIds}}) {
    affected_rows
  }
  insert_award_winners(objects: $inserts) {
    affected_rows
  }
  update_award_winners_many(updates: $updates) {
    affected_rows
  }
}`)

/** Public festivals' results (and films) show in the Archive to everyone. */
export const SetFestivalPublic = gql(`mutation SetFestivalPublic($id: numeric!, $isPublic: Boolean!) {
  update_film_festivals_by_pk(pk_columns: {id: $id}, _set: {is_public: $isPublic}) {
    id
    is_public
  }
}`)

export type AwardTeam = ResultOf<typeof AwardTeam>
export type Award = ResultOf<typeof AwardWinnersYearQuery>['awards'][number]

/** A result being edited; `id` is set once it exists in the database. */
export type DraftRow = {
  key: string
  id?: number
  award_id: number
  team_id: number
  rank: number
}

export type RowStatus = 'new' | 'moved' | undefined

// Matching pasted spreadsheets ------------------------------------------------

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const STOP_WORDS = new Set(['best', 'the', 'of', 'a', 'an', 'and', 'or', 'award', 'for', 'in'])
const words = (s: string) => normalize(s).split(' ').filter(w => w && !STOP_WORDS.has(w))
const stem = (w: string) => w.slice(0, 4)

export type Match<T> = { value?: T; candidates: T[] }

/**
 * Award names in results sheets rarely match ours exactly ("Best Director"
 * for Direction), so fall back to the award sharing the most word stems,
 * preferring awards whose name is mostly covered by the match.
 */
export const matchAward = <T extends { id: number; name?: string | null }>(
  awards: T[],
  cell: string,
): Match<T> => {
  const text = cell.trim()
  if (!text) return { candidates: [] }
  if (/^\d+$/.test(text)) {
    const byId = awards.find(a => a.id === Number(text))
    return { value: byId, candidates: byId ? [byId] : [] }
  }

  const n = words(text).join(' ')
  const exact = awards.filter(a =>
    [a.name ?? '', ...(a.name ?? '').split('/')].some(part => words(part).join(' ') === n),
  )
  if (exact.length === 1) return { value: exact[0], candidates: exact }

  const stems = words(text).map(stem)
  const scored = awards
    .map(award => {
      const awardStems = words(award.name ?? '').map(stem)
      const hits = stems.filter(s => awardStems.includes(s)).length
      return { award, score: hits && hits + hits / awardStems.length }
    })
    .filter(s => s.score > 0)
  const best = _.maxBy(scored, 'score')
  const top = scored.filter(s => s.score === best?.score).map(s => s.award)
  return { value: top.length === 1 ? top[0] : undefined, candidates: top }
}

/** Team numbers, film titles, and team names all identify a film. */
export const matchTeam = <T extends { team_number?: number | null; film_name?: string | null; name?: string | null }>(
  teams: T[],
  cell: string,
): Match<T> => {
  const text = cell.trim()
  if (!text) return { candidates: [] }
  const n = normalize(text)
  const unique = (list: T[]): Match<T> => ({ value: list.length === 1 ? list[0] : undefined, candidates: list })

  // A title that happens to start with a number ("12 Angry Men") wins over the team number.
  const byTitle = teams.filter(t => normalize(t.film_name ?? '') === n)
  if (byTitle.length) return unique(byTitle)
  const byName = teams.filter(t => normalize(t.name ?? '') === n)
  if (byName.length) return unique(byName)

  const number = text.match(/^(?:team\s*)?#?\s*(\d+)(?:$|\s*[-–—:.)|]\s*)/i)
  if (number) return unique(teams.filter(t => t.team_number === Number(number[1])))

  if (n.length < 3) return { candidates: [] }
  return unique(
    teams.filter(t => normalize(t.film_name ?? '').includes(n) || normalize(t.name ?? '').includes(n)),
  )
}

/** Tier labels, rank numbers, or places ("3rd") as written in a results sheet. */
export const matchRank = (cell: string): number | undefined => {
  const text = normalize(cell)
  if (!text) return undefined
  const place = text.match(/^(\d+)(?:st|nd|rd|th)?(?: place)?$/)
  if (place) return placeToRank(Number(place[1]))
  if (/^(win|won|first|gold|1st)/.test(text)) return 1
  if (/^(runner|second|silver|2nd)/.test(text)) return 2
  if (/^(nomin|top (five|5)\b)/.test(text)) return 3
  if (/^(top|finalist|honou?rable)/.test(text)) return 6
  return undefined
}

/** Splits pasted spreadsheet cells; Sheets and Excel copy as tab-separated values. */
export const parseCells = (text: string): string[][] => {
  const lines = text.split(/\r?\n/).filter(line => line.trim())
  if (text.includes('\t')) return lines.map(line => line.split('\t').map(c => c.trim()))

  return lines.map(line => {
    const cells: string[] = []
    let cell = ''
    let quoted = false
    for (let i = 0; i < line.length; i++) {
      const c = line[i]
      if (quoted && c === '"' && line[i + 1] === '"') {
        cell += '"'
        i++
      } else if (c === '"') {
        quoted = !quoted
      } else if (c === ',' && !quoted) {
        cells.push(cell.trim())
        cell = ''
      } else {
        cell += c
      }
    }
    cells.push(cell.trim())
    return cells
  })
}

export type ParsedRow = {
  line: number
  cells: string[]
  awardCell: string
  awardId?: number
  awardCandidates: number[]
  rank?: number
  teamId?: number
  teamCandidates: number[]
  /** Set when the sheet has a result column. */
  resultCell?: string
}

const HEADER = /^(award|category|result|place|rank|tier|team|team number|team name|film|film name|film title|title)$/

/**
 * Reads a pasted results sheet. With three or more columns they're Award,
 * Result, then anything identifying the film; with two, Award and film, with
 * each award's films listed in finishing order. When `awardId` is given every
 * line is a film for that award, in finishing order. Blank award cells repeat
 * the award above, as merged cells copy that way.
 */
export const parseResults = (
  text: string,
  awards: { id: number; name?: string | null }[],
  teams: AwardTeam[],
  awardId?: number,
): ParsedRow[] => {
  const rows = parseCells(text)
  const width = _.max(rows.map(r => r.length)) ?? 0
  const hasResult = !awardId && width >= 3

  let award: Match<{ id: number }> = { candidates: [] }
  let awardCell = ''

  return rows.flatMap((cells, i) => {
    if (i === 0 && cells.some(c => HEADER.test(normalize(c)))) return []

    const teamCells = awardId ? cells : cells.slice(hasResult ? 2 : 1)
    // Slots left empty in the template.
    if (teamCells.every(c => !c)) return []

    const cell = awardId ? String(awardId) : cells[0]
    if (cell && cell !== awardCell) {
      award = matchAward(awards, cell)
      awardCell = cell
    }
    const matches = teamCells.map(c => matchTeam(teams, c))
    const team = matches.find(m => m.value) ?? _.maxBy(matches, m => m.candidates.length)

    const row: ParsedRow = {
      line: i + 1,
      cells,
      awardCell,
      awardId: award.value?.id,
      awardCandidates: award.candidates.map(a => a.id),
      teamId: team?.value?.id,
      teamCandidates: team?.candidates.map(t => t.id) ?? [],
    }
    if (hasResult) {
      row.resultCell = cells[1]
      row.rank = matchRank(cells[1])
    }
    return [row]
  })
}

/** Ranks films listed without a result by their order within each award. */
export const withPlaces = (rows: ParsedRow[]): ParsedRow[] => {
  const places: Record<number, number> = {}
  return rows.map(r => {
    if (r.resultCell != null || !r.awardId) return r
    places[r.awardId] = (places[r.awardId] ?? 0) + 1
    return { ...r, rank: placeToRank(places[r.awardId]) }
  })
}

export const resultKey = (awardId: number, teamId: number) => `${awardId}:${teamId}`

/**
 * Why each row can't be imported as-is, if anything. Imports only add, so a
 * film already entered for an award (`existing`, by `resultKey`) is skipped.
 */
export const rowProblems = (rows: ParsedRow[], existing = new Set<string>()) => {
  const counts = _.countBy(rows.filter(r => r.awardId && r.teamId), r => resultKey(r.awardId!, r.teamId!))
  return rows.map(r => {
    if (!r.awardId)
      return r.awardCandidates.length
        ? `"${r.awardCell}" matches several awards`
        : r.awardCell ? `Unknown award "${r.awardCell}"` : 'Missing award'
    if (!r.rank)
      return r.resultCell == null
        ? `More than ${TOP_TEN} films for this award`
        : r.resultCell ? `Unknown result "${r.resultCell}"` : 'Missing result'
    if (!r.teamId) return r.teamCandidates.length ? 'Matches several films' : 'No matching film'
    const key = resultKey(r.awardId, r.teamId)
    if (existing.has(key)) return 'Already entered for this award'
    if (counts[key] > 1) return 'Listed more than once for this award'
    return undefined
  })
}

/** A blank results sheet, ready to fill in and paste back. */
export const resultsTemplate = (awards: { id: number; name?: string | null }[]) =>
  [
    ['Award', 'Result', 'Team #', 'Film'],
    ...sortAwards(awards).flatMap(a =>
      TIERS.flatMap(t => _.times(t.slots, () => [a.name ?? '', t.label, '', ''])),
    ),
  ]
    .map(r => r.join('\t'))
    .join('\n')
